import React, { useRef, useCallback } from 'react'
import { GroupBase, SelectComponentsConfig } from 'react-select'
import Select from 'react-select/dist/declarations/src/Select'
import styled from 'styled-components'
import { useInView } from '../../../hooks'
import { BasicSelectProps, StyledMenuList } from '../BasicSelect/BasicSelect'

const MenuListScrolledArea = styled.div`
  position: relative;
`
const BottomAnchor = styled.div<{
  maxHeight: React.CSSProperties['maxHeight']
}>`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: ${({ maxHeight }) => maxHeight};
  z-index: -1;
`

type UseIntersectionObserverForSelectPropsType<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Partial<Pick<BasicSelectProps<Option, IsMulti, Group>, 'onMenuOpen' | 'onMenuClose' | 'menuMaxHeight'>> & {
  /**
   * Must be memoized!
   */
  onMenuScrollToBottom?: () => void
  /**
   * Must be number from 0 to 1.
   * @default 0.05
   * */
  scrollToBottomThreshold?: number
}

export const useIntersectionObserverForSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  onMenuScrollToBottom,
  scrollToBottomThreshold = 0.5,
  onMenuOpen,
  onMenuClose,
  menuMaxHeight,
}: UseIntersectionObserverForSelectPropsType<Option, IsMulti, Group>) => {
  const selectRef = useRef<Select<Option, IsMulti, Group> | null>(null)

  const instancePrefix = (selectRef.current as any)?.instancePrefix
  const bottomAnchorId = `${instancePrefix}-bottom-anchor`
  const MenuList: SelectComponentsConfig<Option, IsMulti, Group>['MenuList'] = useCallback(
    ({ children, ...props }) => (
      <StyledMenuList {...props} maxHeight={menuMaxHeight}>
        <MenuListScrolledArea>
          {children}
          {
            // This is needed to make better onMenuScrollToBottom behavior with intersection observer. See https://agroclub.atlassian.net/browse/ENT-3525 for details.
          }
          <BottomAnchor maxHeight={menuMaxHeight} id={bottomAnchorId} />
        </MenuListScrolledArea>
      </StyledMenuList>
    ),
    [menuMaxHeight, bottomAnchorId],
  )

  const { ref } = useInView(onMenuScrollToBottom, scrollToBottomThreshold)

  // We need additional logic with observers only if onMenuScrollToBottom is provided.
  const handleMenuOpen =
    onMenuScrollToBottom && selectRef.current && instancePrefix
      ? () => {
          onMenuOpen?.()
          // This is because menuListRef is not available in onMenuOpen callback immediately.
          setTimeout(() => {
            const bottomAnchor = selectRef.current?.menuListRef ? document.getElementById(bottomAnchorId) : null
            bottomAnchor && ref(bottomAnchor)
          }, 1)
        }
      : undefined

  const handleMenuClose = onMenuScrollToBottom
    ? () => {
        onMenuClose?.()
        // we need to stop observing the bottom anchor element when the menu is closed to prevent memory leaks.
        ref(null)
      }
    : undefined

  return {
    ref: selectRef,
    components: { MenuList },
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
  }
}
